<template>
  <div
    id="bottom-components-bar"
    class="mixes-bottom-bar lg:hidden sticky-bottom bg-white"
  >
    <BaseWave
      v-if="BrowserService.isMobile()"
      path="footerMobile"
      color="text-white"
      class="relative"
      svg-class="icon align-bottom mixes-bottom-bar__wave"
    />
    <div class="mixes-bottom-bar__wrapper">
      <div class="container relative py-1">
        <!-- ingredients -->
        <div class="row flex-nowrap ml-n2 mr-0 w-100 py-1 flex-grow-1 h-auto list-unstyled">
          <div class="mix-composition-row flex-1 flex relative">
            <Swiper
              id="js-mobile-bottom-bar"
              class="form-row list-unstyled js-component-list-mobile flex-1"
              slides-per-view="auto"
              :touch-start-prevent-default="false"
              :scrollbar="{ draggable: true, hide: false }"
            >
              <SwiperSlide
                v-for="(item, index) in getMixItems"
                :key="index"
                class="col-25 pl-2 pr-0 mixes-bottom-bar__item"
                :class="[
                  { 'mixes-bottom-bar__item--muted': hoveredIndex && index.toString() !== hoveredIndex },
                  { 'mixes-bottom-bar__item--unmuted': index.toString() === hoveredIndex },
                ]"
                @mouseenter="onHover(index.toString())"
                @mouseleave="resetHover"
              >
                <TubeItemMobile
                  :item-index="index.toString()"
                  :hovered-index="hoveredIndex"
                  :kind="kind"
                  :item="item"
                />
              </SwiperSlide>
              <!-- blank slides -->
              <SwiperSlide
                v-if="getMixItems && getMixItems.length - 5 > 0"
                class="col-25 pl-2 pr-0 mixes-bottom-bar__item"
              />
            </Swiper>
            <div
              v-if="getMixItems && getMixItems.length - 5 > 0 && getMixItems.length - 4 - swiper.activeIndex > 0"
              class="more-ingredients events-none absolute"
            >
              <button
                type="button"
                class="text-primary events-all"
                @click="nextSlide"
              >
                +{{ getMixItems.length - 4 - swiper.activeIndex }}
              </button>
            </div>
          </div>
          <div class="nutrition-btn-wrapper relative">
            <VDropdown
              theme="nutritiontable"
              :shown="!hideNutritionTable"
              @apply-hide="onHide"
            >
              <span
                class="nutrition-btn bg-primary-700 text-white relative inline-block"
                @click="
                  () => {
                    hideNutritionTable = false
                  }
                "
              >
                <i class="mi-info icon-16" />
              </span>
              <template #popper>
                <div class="bg-primary-700 pb-2">
                  <div class="block px-4 pt-3 pb-2">
                    <h3 class="text-white m-0">
                      {{ t('nutritional_values') }}
                    </h3>
                  </div>
                  <p
                    v-if="!getMixItems || !getMixItems.length"
                    class="text-white m-0 px-4 pb-3"
                  >
                    {{ t('empty_mix') }}
                  </p>
                  <TubeNutritionTable
                    :show="getMixItems && getMixItems.length"
                    :mix="getActualMix"
                  />
                </div>
              </template>
            </VDropdown>
          </div>
        </div>
        <!-- price, grammage, amount, add, finish -->
        <div class="form-row items-center justify-between py-1">
          <!-- price, grammage -->
          <div class="row px-3 text-nowrap">
            <div class="col">
              <p class="mb-0 text-lg font-bold">
                {{ numberToCurrency(getActualMix?.totalPrice, t('currency')) }}
              </p>
              <p class="mb-0 text-xs text-muted">
                {{ getActualMix?.totalWeight.toFixed(0) }} g
              </p>
            </div>
            <div v-show="kind === 'musli' || kind === 'porridge'">
              <VTooltip theme="checkoutTooltip">
                <button
                  class="btn btn-outline-warning p-1 rounded-circle"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="info"
                  type="button"
                  aria-label="info"
                  tabindex="-1"
                >
                  <i class="mi-question icon-16" />
                </button>
                <template #popper>
                  <span>{{ t('weight_of_mix_varies') }}</span>
                </template>
              </VTooltip>
            </div>
          </div>
          <!-- finish -->
          <div
            v-if="getEditedMix"
            class="flex flex-col pt-1"
          >
            <button
              type="button"
              class="btn  btn-success py-3 my-2 px-5 font-medium rounded-pill"
              :disabled="insertionInProgress"
              @click="addToCart"
            >
              <span class="block flex-grow-1">{{ t('save_mix') }}</span>
              <i class="mi-arrow-r icon-20 mi-no-mx ml-1" />
            </button>
            <button
              type="button"
              class="btn  btn-danger py-3 px-5 font-medium rounded-pill"
              :disabled="insertionInProgress"
              @click="cancelEdit"
            >
              <span class="block flex-grow-1">{{ t('cancel_edit') }}</span>
              <i class="mi-times icon-20 mi-no-mx ml-1" />
            </button>
          </div>
          <div
            v-else
            class="col-auto"
          >
            <button
              type="button"
              class="btn btn-success  px-5 rounded-pill font-medium"
              @click="addToCart"
            >
              {{ t('finish') }}
              <i class="mi-arrow-r icon-20 mi-no-mx ml-3" />
            </button>
          </div>
        </div>
        <!-- scroll to top -->
        <a
          href="#top"
          class="btn p-2 absolute centered-horizontal top-0 mt-n5 rounded-circle bg-white opacity-50 bg-white"
          aria-label="Scroll up"
          @click="scrollUp"
        >
          <i class="mi-triangle-u icon-10 text-primary" />
        </a>
      </div>
    </div>
  </div>
  <!-- bottom gap because of footer waves -->
  <div class="py-4 bg-white lg:hidden" />
</template>

<script setup>
import { storeToRefs } from 'pinia'
import SwiperCore from 'swiper'
import { Scrollbar } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import TubeItemMobile from '@/components/mix/TubeItemMobile.vue'
import TubeNutritionTable from '@/components/mix/TubeNutritionTable.vue'
import { numberToCurrency } from '@/mixins/numberToCurrency'
import BrowserService from '@/services/browserService'
import { CartService } from '@/services/cartService'
import MixDefaultNamesService from '@/services/mixDefaultNamesService'
import { useMixStore } from '@/stores/MixStore'
import { useModalStore } from '@/stores/ModalStore'
import MixValidator from '@/validators/mixValidator'

const BaseWave = defineAsyncComponent(() => import('@/components/general/BaseWave.vue'))

const props = defineProps({
  kind: {
    type: String,
    default: 'musli',
  },
})

const { openModal } = useModalStore()
const mixStore = useMixStore()
const { getEditedMix, getActualMix, getMixItems, getMixLength } = storeToRefs(mixStore)
const { t, locale } = useI18n()

SwiperCore.use([Scrollbar])

const hoveredIndex = ref('')
const swiper = ref()
const insertionInProgress = ref(false)
const hideNutritionTable = ref(true)

const onHide = (value) => {
  hideNutritionTable.value = value
}

window.addEventListener('scroll', () => {
  onHide(true)
})

const scrollUp = (event) => {
  const targetElement = document.querySelector('#top')
  if (targetElement) {
    event.preventDefault()
    const targetOffsetTop = targetElement.getBoundingClientRect().top + document.documentElement.scrollTop
    window.scrollTo({ top: targetOffsetTop, behavior: 'smooth' })
  }
}

const cancelEdit = () => {
  mixStore.cancelEditMix(mixStore.getEditedMix.kind)
  openModal({
    id: 'mix-added-to-cart-modal',
    title: t('mix_edit_canceled'),
  })
}

const addToCart = async () => {
  if (!mixStore.getActualMix) {
    openModal({
      id: 'modal-alert',
      title: t('mix_does_not_have_any_items'),
    })
    return
  }
  insertionInProgress.value = true
  const validator = new MixValidator(mixStore.getActualMix)
  mixStore.getActualMix.state = 'closed'
  if (validator.isValid()) {
    if (!mixStore.getActualMix.name) mixStore.getActualMix.name = MixDefaultNamesService.randomName(locale.value)
    const cartService = new CartService()
    const data = mixStore.getActualMix.serializeMix()
    if (mixStore.getEditedMix) {
      await cartService.updateMix(
        data,
        mixStore.getActualMix.kind,
        mixStore.getActualMix.quantity,
        mixStore.getEditedMix.hash,
      )
      mixStore.cancelEditMix(mixStore.getEditedMix.kind)
      openModal({
        id: 'mix-added-to-cart-modal',
        title: t('mix_saved'),
        params: {
          hash: mixStore.getActualMix.hash,
        },
      })
    } else {
      await cartService.addMix(data, mixStore.getActualMix.kind, mixStore.getActualMix.quantity)
      openModal({
        id: 'mix-added-to-cart-modal',
        title: t('mix_was_added_to_cart'),
        params: {
          hash: mixStore.getActualMix.hash,
        },
      })
    }
    mixStore.resetMix(props.kind)
    insertionInProgress.value = false
  } else {
    mixStore.getActualMix.state = 'open'
    const [error] = validator.getErrors()
    const message = t(`${error.key}`, error.data)
    openModal({
      id: 'modal-alert',
      title: message,
    })
    insertionInProgress.value = false
  }
}

const onHover = (uid) => {
  hoveredIndex.value = uid
}

const resetHover = () => {
  hoveredIndex.value = ''
}

const nextSlide = () => {
  swiper.value.slideNext()
}

onMounted(() => {
  swiper.value = document.querySelector('#js-mobile-bottom-bar').swiper
  setTimeout(() => {
    swiper.value.scrollbar?.updateSize()
  }, 1000)
})

watch(
  () => getMixLength.value,
  () => swiper.value.scrollbar?.updateSize(),
)
</script>

<style lang="scss">
.more-ingredients {
  z-index: 3;
  right: 0;
  top: 0;
  position: absolute;
  text-align: right;
  width: 40%;
  height: 100%;

  button {
    position: relative;
    z-index: 3;
    font-weight: 500;
    font-size: 24px;
    border: none;
    outline: none;
    background-color: rgba(255, 255, 255, 0);
    display: inline-block;
    padding: 4px 14px;
    border-right: 1px solid #4F2B1B;
    margin-right: 16px;
  }

  &:before {
    content: "";
    position: absolute;
    z-index: 2;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff, #ffffff);
    right: 0;
    bottom: 3px;
    width: 100%;
    height: 151%;
  }
}
.nutrition-btn-wrapper {
  z-index: 2;

  button {
    z-index: 2;
  }

  &:before {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 400%;
    height: 151%;
    background-color: $white;
  }
}
.flex-1 {
  flex: 1;
}
.nutrition-btn {
  border: none;
  outline: none;
  border-radius: 100%;
  padding: 6px 6px;
}
.col-25{
	@include make-col(2.25, $grid-columns);
}
</style>

<i18n>
  {
    "cs": {
    "base_not_found": "Nejprve vložte základ mixu. Najdete ho úplně nahoře pod ikonou “základ”.",
    "cancel_edit": "Zrušit editaci",
    "currency": "Kč",
    "empty_mix": "Pro zobrazení nutričních hodnot vložte alespoň jednu položku do mixu.",
    "finish": "Dokončit",
    "first_insert_base_of_mix": "Nejprve vložte základ mixu. Najdete ho úplně nahoře pod ikonou “základ”",
    "mix_cant_have_more_than_1_base": "Mix už má základ.",
    "mix_does_not_have_any_items": "Mix je prázdný. Nejprve přidejte nějaké dobroty.",
    "mix_edit_canceled": "Editace mixu byla zrušena",
    "mix_has_less_items_than_x": "Mix má méně položek než {current_count}. Nejprve ho prosím naplňte.",
    "mix_is_not_full_enough": "Před odesláním naplňte tubus alespoň na {max_volume} % jeho objemu. Nyní je tubus zaplněn z {current_volume} %.",
    "mix_saved": "Mix byl uložen",
    "mix_was_added_to_cart": "Mix byl vložen do košíku",
    "nutritional_values": "Nutriční hodnoty",
    "save_mix": "Uložit mix",
    "weight_of_mix_varies": "Váha mixu se mění na základě objemu zvolených ingrediencí, aby byl tubus vždy plný. Celková váha se tedy může při vašem míchání zvyšovat i snižovat."
},
    "sk": {
    "base_not_found": "Najprv vložte základ mixu. Nájdete ho úplne hore pod ikonou \"základ\".",
    "cancel_edit": "Zrušiť editáciu",
    "currency": "€",
    "empty_mix": "Pre zobrazenie nutričných hodnôt vložte aspoň jednu položku do mixu.",
    "finish": "Dokončiť",
    "first_insert_base_of_mix": "Najprv vložte základ mixu. Nájdete ho úplne hore pod ikonou \"základ\"",
    "mix_cant_have_more_than_1_base": "Mix už základ má.",
    "mix_does_not_have_any_items": "Mix je prázdny. Najprv pridajte nejaké dobroty.",
    "mix_edit_canceled": "Editácia mixu bola zrušená",
    "mix_has_less_items_than_x": "Mix má menej položiek ako {current_count}. Najprv ho prosím naplňte.",
    "mix_is_not_full_enough": "Pred odoslaním naplňte tubus aspoň na {max_volume} % jeho objemu. Teraz je tubus zaplnený z {current_volume} %.",
    "mix_saved": "Mix bol uložený",
    "mix_was_added_to_cart": "Mix bol vložený do košíka",
    "nutritional_values": "Nutričné hodnoty",
    "save_mix": "Uložiť mix",
    "weight_of_mix_varies": "Váha mixu sa mení na základe objemu zvolených ingrediencií tak, aby bol tubus vždy plný. Celková váha sa preto môže pri vašom miešaní zvyšovať aj znižovať."
},
    "pl": {
    "base_not_found": "Najpierw wybierz podstawę mieszanki. Możesz ją znaleźć na samej górze pod ikoną „Podstawa musli”.",
    "cancel_edit": "Anuluj zmiany",
    "currency": "zł",
    "empty_mix": "Aby zobaczyć wartości odżywcze, dodaj co najmniej jeden produkt do mieszanki.",
    "finish": "Dokończ",
    "first_insert_base_of_mix": "Najpierw włóż podstawę mixu. Znajdziesz ją na górze pod ikoną \"podstawa\"",
    "mix_cant_have_more_than_1_base": "Mix już ma podstawę.",
    "mix_does_not_have_any_items": "Mix jest pusty. Najpierw dodaj ulubione smakołyki.",
    "mix_edit_canceled": "Edycja miksu została anulowana",
    "mix_has_less_items_than_x": "Mix ma mniej składników niż {current_count}. Najpierw go uzupełnij.",
    "mix_is_not_full_enough": "Przed odesłaniem wypełnij tubę przynajmniej do {max_volume}% jej objętości. Teraz w tubie znajduje się tylko {current_volume}%.",
    "mix_saved": "Mieszanka została zapisana",
    "mix_was_added_to_cart": "Mix został włożony do koszyka",
    "nutritional_values": "Wartości odżywcze",
    "save_mix": "Zapisz mieszankę",
    "weight_of_mix_varies": "Waga mieszanki zmienia się w zależności od objętości wybranych składników, tak, aby tuba była zawsze pełna. Całkowita waga może się więc zwiększać lub zmniejszać w miarę mixowania."
},
    "hu": {
    "base_not_found": "Először válaszd ki az alapot, az oldal tetején.",
    "cancel_edit": "Kosár",
    "currency": "Ft",
    "empty_mix": "A tápértékek megtekintéséhez tegyen legalább egy alapanyagot a mixbe.",
    "finish": "Befejezni",
    "first_insert_base_of_mix": "Először az alapot add hozzá. A lap tetején találod meg, a müzli feliratnál.",
    "mix_cant_have_more_than_1_base": "A mixnek már van alapja.",
    "mix_does_not_have_any_items": "A mix üres. Először válassz valamilyen finomságot.",
    "mix_edit_canceled": "A keverék szerkesztése megszakítva",
    "mix_has_less_items_than_x": "A mixnek kevesebb összetevője van, mint {current_count}. Először kérlek egészítsd ki.",
    "mix_is_not_full_enough": "Kosárba helyezés előtt töltsd meg a tubus legalább {max_volume} %-át. Jelenleg a tubus {current_volume} %-ig van tele.",
    "mix_saved": "A keverék elmentve",
    "mix_was_added_to_cart": "A mix a kosárba lett helyezve",
    "nutritional_values": "Tápértékek adatok",
    "save_mix": "Mentés",
    "weight_of_mix_varies": "A mix súlya a kiválasztott összetevôk térfogatától függ, annak érdekében, hogy a tubus mindig tele legyen. A végsô súly tehát nôhet vagy csökkenhet ahogy kevered."
},
    "de": {
    "base_not_found": "Wähle zunächst die Basis. Du findest sie ganz oben unter dem Symbol \"Basis\"",
    "cancel_edit": "Bearbeitung abbrechen",
    "currency": "EUR",
    "empty_mix": "Um die Nährwerte anzuzeigen, füge dem Mix mindestens eine Zutat hinzu.",
    "finish": "Beenden",
    "first_insert_base_of_mix": "Wähle zunächst die Basis. Diese findest du ganz oben unter dem Symbol \"Basis\"",
    "mix_cant_have_more_than_1_base": "Der Mix hat bereits eine Basis.",
    "mix_does_not_have_any_items": "Dein Mix ist leer. Füge zunächst einige Leckereien hinzu.",
    "mix_edit_canceled": "Mix bearbeiten wurde abgebrochen",
    "mix_has_less_items_than_x": "Der Mix hat weniger Artikel als {current_count}. Bitte fülle ihn zuerst.",
    "mix_is_not_full_enough": "Fülle die Dose vor dem Versand auf mindestens{max_volume}% seines Volumens. Jetzt ist die Dose zu% gefüllt. {current_volume}",
    "mix_saved": "Der Mix wurde gespeichert",
    "mix_was_added_to_cart": "Mix wurde in den Warenkorb gelegt",
    "nutritional_values": "Nährwertangaben",
    "save_mix": "Mix speichern",
    "weight_of_mix_varies": "Das Gewicht des Mix ändert sich je nach der Menge der ausgewählten Zutaten, um sicherzustellen, dass die Dose immer voll ist. Das Gesamtgewicht kann daher beim Mischen steigen oder fallen."
},
    "at": {
    "base_not_found": "Wähle zunächst die Basis. Du findest sie ganz oben unter dem Symbol \"Basis\"",
    "cancel_edit": "Bearbeitung abbrechen",
    "currency": "EUR",
    "empty_mix": "Um die Nährwerte anzuzeigen, füge dem Mix mindestens eine Zutat hinzu.",
    "finish": "Beenden",
    "first_insert_base_of_mix": "Wähle zunächst die Basis. Diese findest du ganz oben unter dem Symbol \"Basis\"",
    "mix_cant_have_more_than_1_base": "Der Mix hat bereits eine Basis.",
    "mix_does_not_have_any_items": "Dein Mix ist leer. Füge zunächst einige Leckereien hinzu.",
    "mix_edit_canceled": "Mix bearbeiten wurde abgebrochen",
    "mix_has_less_items_than_x": "Der Mix hat weniger Artikel als {current_count}. Bitte fülle ihn zuerst.",
    "mix_is_not_full_enough": "Fülle die Dose vor dem Versand auf mindestens{max_volume}% seines Volumens. Jetzt ist die Dose zu% gefüllt. {current_volume}",
    "mix_saved": "Der Mix wurde gespeichert",
    "mix_was_added_to_cart": "Mix wurde in den Warenkorb gelegt",
    "nutritional_values": "Nährwertangaben",
    "save_mix": "Mix speichern",
    "weight_of_mix_varies": "Das Gewicht des Mix ändert sich je nach der Menge der ausgewählten Zutaten, um sicherzustellen, dass die Dose immer voll ist. Das Gesamtgewicht kann daher beim Mischen steigen oder fallen."
},
    "ro": {
    "base_not_found": "Mai întâi alege baza. Le găsești în partea de sus a paginii.",
    "cancel_edit": "Anulează editarea",
    "currency": "Lei",
    "empty_mix": "Pentru a vizualiza valorile nutriționale, adăugați cel puțin un produs în mix.",
    "finish": "Termină",
    "first_insert_base_of_mix": "Mai întâi adaugă baza. O găsești în partea de sus a paginii, sub denumirea “musli”",
    "mix_cant_have_more_than_1_base": "Amestecul conține deja un produs de bază.",
    "mix_does_not_have_any_items": "Amestecul e gol. Mai întâi adaugă niște bunătăți.",
    "mix_edit_canceled": "Editarea mixului a fost anulată",
    "mix_has_less_items_than_x": "Mixul are mai puține articole decât {current_count}. Vă rugăm să îl umpleți mai întâi.",
    "mix_is_not_full_enough": "Înainte de adăugarea în coș umple tubul la cel puțin {max_volume} % din volumul lui. Momentan {current_volume} % din tub e plin.",
    "mix_saved": "Amestecul a fost salvat",
    "mix_was_added_to_cart": "Mixul a fost adăugat în coș",
    "nutritional_values": "Valori nutriționale",
    "save_mix": "Salvează amestecul",
    "weight_of_mix_varies": "Greutatea mixului se modifică în funcție de volumul ingredientelor selectate, astfel încât tubul să fie întotdeauna plin. Greutatea totală poate crește sau scădea pe măsură ce amesteci."
},
    "fr": {
    "base_not_found": "Commencez par ajouter la base du mix. Vous la trouverez tout en haut sous l’icône « base ».",
    "cancel_edit": "Annuler la modification",
    "currency": "€",
    "empty_mix": "Pour afficher les valeurs nutritionnelles, ajoutez au moins un article au mix.",
    "finish": "Terminer",
    "first_insert_base_of_mix": "Commencez par ajouter la base du mix. Vous la trouverez tout en haut sous l’icône « base ».",
    "mix_cant_have_more_than_1_base": "Le mix a déjà une base.",
    "mix_does_not_have_any_items": "Le mix est vide. Ajoutez d’abord quelques gourmandises.",
    "mix_edit_canceled": "L’édition du mix a été annulée",
    "mix_has_less_items_than_x": "Mix contient moins d'articles que{current_count}. Veuillez d'abord le remplir.",
    "mix_is_not_full_enough": "Avant l’expédition, remplissez le tube au moins à {max_volume} % de sa capacité. Actuellement, le tube est rempli à%. {current_volume}",
    "mix_saved": "Le mix a été enregistré",
    "mix_was_added_to_cart": "Le mix a été ajouté au panier",
    "nutritional_values": "Valeurs nutritionnelles",
    "save_mix": "Enregistrer le mix",
    "weight_of_mix_varies": "Le poids du mélange varie en fonction du volume des ingrédients sélectionnés, de sorte que le tube soit toujours plein. Ainsi, le poids total peut à la fois augmenter et diminuer au fur et à mesure que vous créez."
}
  }
</i18n>
